import api from '@/services/api'
import { onlyDigits, maskCep, maskCnpj } from '@/components/PjAgrada/pjAgradaFormat'
import type { PjAgradaFormData } from '@/components/PjAgrada/pjAgradaTypes'

type CepLookupResult = {
  cep: string
  logradouro: string
  complemento: string
  bairro: string
  cidade: string
  uf: string
}

type CnpjLookupResult = {
  cnpj: string
  razaoSocial: string
  nomeFantasia: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cep: string
  cidade: string
  uf: string
  telefone: string
  email: string
  atividadePrincipal?: string
  situacao?: string
  naturezaJuridica?: string
}

export function applyCepToPjForm(form: PjAgradaFormData, cep: CepLookupResult): PjAgradaFormData {
  return {
    ...form,
    cep: maskCep(cep.cep),
    logradouro: cep.logradouro || form.logradouro,
    complemento: cep.complemento || form.complemento,
    bairro: cep.bairro || form.bairro,
    cidade: cep.cidade || form.cidade,
    uf: cep.uf || form.uf,
  }
}

/** Preenche só dados da empresa/endereço — nunca nome, CPF, telefone ou e-mail pessoais. */
export function applyCnpjToPjForm(form: PjAgradaFormData, cnpj: CnpjLookupResult): PjAgradaFormData {
  return {
    ...form,
    cnpj: maskCnpj(cnpj.cnpj),
    razaoSocial: cnpj.razaoSocial || form.razaoSocial,
    cep: cnpj.cep || form.cep,
    logradouro: cnpj.logradouro || form.logradouro,
    numero: cnpj.numero || form.numero,
    complemento: cnpj.complemento || form.complemento,
    bairro: cnpj.bairro || form.bairro,
    cidade: cnpj.cidade || form.cidade,
    uf: cnpj.uf || form.uf,
  }
}

export async function lookupPjCep(cep: string): Promise<CepLookupResult> {
  const digits = onlyDigits(cep)
  const resp = await api.get(`/pj-agrada/lookup/cep/${digits}`)
  return resp.data.data
}

export async function lookupPjCnpjPublic(cnpj: string): Promise<CnpjLookupResult> {
  const digits = onlyDigits(cnpj)
  const resp = await api.get(`/pj-agrada/lookup/cnpj/${digits}`)
  return resp.data.data
}

export async function lookupPjCnpjAuth(cnpj: string): Promise<CnpjLookupResult> {
  const digits = onlyDigits(cnpj)
  const resp = await api.get(`/empresas/cnpj/${digits}?source=brasilapi`)
  const data = resp.data.data ?? resp.data
  return {
    cnpj: (data.cnpj ?? digits).replace(/\D/g, ''),
    razaoSocial: data.razaoSocial ?? '',
    nomeFantasia: data.nomeFantasia ?? data.nome ?? '',
    logradouro: data.endereco?.split(',')[0]?.trim() ?? data.logradouro ?? '',
    numero: data.numero ?? '',
    complemento: data.complemento ?? '',
    bairro: data.bairro ?? '',
    cep: onlyDigits(data.cep ?? ''),
    cidade: data.cidade ?? '',
    uf: (data.estado ?? data.uf ?? '').toUpperCase(),
    telefone: data.telefone ?? '',
    email: data.email ?? '',
  }
}
